import * as cx from 'classnames';
import { History, Location } from 'history';
import {
    SEO_DESCRIPTION_DEFAULT_FALLBACK,
    SEO_TITLE_DEFAULT_FALLBACK,
    SEO_TITLE_HOMEPAGE,
    SEO_TITLE_title_TEMPLATE_WITH_BRANDING,
} from 'mk/autogenerated/translations/Page.ccb16de2a94c61087a5f6623458cc1d0'
import { ACTIVE_SERVER_ID } from 'mk/settings';
import BoTabs from 'mk2/apps/bo/components/BoTabs';
import * as usersUrls from 'mk2/apps/users/urls';
import { Footer } from 'mk2/components/Footer';
import Header from 'mk2/components/Header';
import ScrollToTop from 'mk2/components/ScrollToTop';
import AdSlotBranding from 'mk2/containers/AdSlotBranding/AdSlotBranding';
import AdSlotSticky from 'mk2/containers/AdSlotSticky/AdSlotSticky';
import { AppName } from 'mk2/containers/Application/Application';
import PhoneVerificationModal from 'mk2/containers/PhoneVerificationModal/PhoneVerificationModal';
import PrivacyReminder from 'mk2/containers/PrivacyReminder/PrivacyReminder';
import SiteMenu from 'mk2/containers/SiteMenu/SiteMenu';
import { hasRequiredPermissions } from 'mk2/helpers/permissions';
import { organizationModrykonik } from 'mk2/helpers/structuredData';
import { url } from 'mk2/helpers/urls';
import PageDefinition from 'mk2/pages/Page';
import { isFailure, LoadingState } from 'mk2/reducers/loadingState';
import { redirectInPWA } from 'mk2/services/browserHistory';
import { interpolate } from 'mk2/services/i18n';
import * as React from 'react';
import Helmet from 'react-helmet';
import { StickyContainer } from 'react-sticky';
import styles from './Page.mscss';

interface ChildrenProps {
    app: AppName;
    content: (JSX.Element | null | false);
    rightContent: (JSX.Element | null | false);
    halfpage: (JSX.Element | null | false);
}

interface OwnProps {
    className?: string;
    app: AppName;
    pageInstance: PageDefinition<any, any, any>;
    location: Location;
    history: History;
    baseUrl: string;
    children: (props: ChildrenProps) => React.ReactNode;
    loadingState?: LoadingState;
    mobile: boolean;
    requestUserPermissions: string[];
}

interface OwnState {
    hasPermission: boolean;
}

type Props = OwnProps;

class Page extends React.PureComponent<Props, OwnState> {

    public static getDerivedStateFromProps(props: Props, state: OwnState) {
        const { requestUserPermissions, pageInstance } = props;

        const permissions: string | string[] | boolean = pageInstance.getPermissionRequired(props);
        const hasPermission = hasRequiredPermissions(requestUserPermissions, permissions);

        return state.hasPermission !== hasPermission
            ? { hasPermission }
            : null;  // nothing tu update
    }

    constructor(props: Props) {
        super(props);

        this.state = {
            hasPermission: true,
        };
    }

    public componentDidMount() {
        this.redirectIfMissingPermission();

    }
    public componentDidUpdate(prevProps: Props) {
        this.redirectIfMissingPermission();
    }

    public render() {
        const { className, pageInstance, app, ...rest } = this.props;
        const { baseUrl, location } = this.props;

        if (!this.state.hasPermission) {
            if (typeof window === 'undefined') {
                // CeMi: zatial neviem o ziadnom inom rozumnom sposobe ako pustat side-effecty pre SSR
                // (ked sa nezavola componentDidMount alebo componentDidUpdate)
                this.redirectIfMissingPermission();
            }

            // do not render inner content, until browser handles the url redirect
            return null;
        }

        // If loadingState is failure - disable ads
        const failureLoadingState = isFailure(rest.loadingState);

        const adKey = hashCode(`${location.pathname}${location.search.replace(/[?&]?(q|search)=[^&]+/, '')}`);

        const halfpageProps = pageInstance.getHalfpageProps(rest);
        const halfpage = halfpageProps && !failureLoadingState ? (
            <AdSlotSticky key={adKey} {...halfpageProps} />
        ) : null;

        const brandingProps = pageInstance.getBrandingProps(rest);
        const branding = brandingProps && !failureLoadingState ? (
            <AdSlotBranding key={adKey} {...brandingProps} />
        ) : null;

        const title = pageInstance.getMetaTitle(rest);
        const description = pageInstance.getMetaDescription(rest);
        const currentUrl = `${location.pathname}${location.search}`;
        const opengraphData = pageInstance.getOpenGraphData(rest, baseUrl, currentUrl);
        const structuredData = pageInstance.getStructuredData(rest);

        const headerTitle = pageInstance.getHeaderTitle(rest);
        const headerLeft = pageInstance.getHeaderLeft(rest);
        const headerRight = pageInstance.getHeaderRight(rest);
        const tabs = pageInstance.getHeaderTabs(rest);

        const pageHeader = pageInstance.hasPageHeaderPart1(rest) && (app !== 'bo' ? (
            <SiteMenu
                menuSelection={pageInstance.getHeaderMenuSelection(app, rest)}
                fluxibleCompatibility={false}
                currentUrl={currentUrl}
                showHeader={pageInstance.hasPageHeaderPart2(rest)}
                isHeaderSticky={pageInstance.isHeaderSticky(rest)}
                headerHasBackBtn={pageInstance.hasHeaderBackButton(rest)}
                headerTitle={headerTitle}
                headerLeft={headerLeft}
                headerRight={headerRight}
                headerTabs={tabs}
                mobileShowLogo={pageInstance.hasMobileLogo(rest)}
            />
        ) : (
            <BoTabs
                menuSelection={pageInstance.getHeaderMenuSelection(app, rest)}
                fluxibleCompatibility={false}
            />
        ));

        const appHeader = (!rest.mobile && (headerTitle || headerLeft || headerRight || (tabs && tabs.length))) ? (
            <div className={styles.Page__header__content}>
                <Header
                    heading={headerTitle}
                    left={headerLeft}
                    right={headerRight}
                    showLogo={ACTIVE_SERVER_ID === 207 && pageInstance.hasMobileLogo(rest)}
                />

                {tabs && tabs.length && (
                    <div className={styles.Page__header__tabs}>
                        {tabs.map((tab, index) => tab && (
                            <div key={index} className={styles.Page__header__tab}>{tab}</div>
                        ))}
                    </div>
                )}
            </div>
        ) : null;

        const contentFooter = pageInstance.getFooter(rest);

        const content = (
            <div className={styles.Page__content}>
                {pageInstance.hasPageHeaderPart2(rest) && (
                    <div className={styles.Page__header}>
                        {(!rest.mobile) ? (
                            appHeader
                        ) : null}
                    </div>
                )}

                {pageInstance.render(rest)}

                {contentFooter && (
                    <div className={styles.Page__content__footer}>
                        {contentFooter}
                    </div>
                )}
            </div>
        );

        const rightContent = pageInstance.renderRight(rest);

        return (
            <div className={cx(styles.Page, className)}>
                <Helmet>
                    <title>{title
                        // kvoli seo chceme, aby kazda stranka mala vzdy <title> a aby na konci title bolo
                        // '- Modry konik'. Google vie sam pridat '- Modry konik', ale pre Seznam to musime
                        // explicitne pridat my
                        ? (title !== SEO_TITLE_HOMEPAGE
                            ? interpolate(SEO_TITLE_title_TEMPLATE_WITH_BRANDING, {title})
                            : title // homepage uz ma 'Modry konik -' na zaciatku
                        ) : SEO_TITLE_DEFAULT_FALLBACK
                    }</title>
                    <meta name="description" content={description || SEO_DESCRIPTION_DEFAULT_FALLBACK} />
                    <meta name="robots" content={pageInstance.getMetaRobots(rest)} />
                    {opengraphData && Object.keys(opengraphData).map((ogKey: string) => (
                        <meta key={ogKey} property={ogKey} content={opengraphData[ogKey]} />
                    ))}
                    <script type="application/ld+json">
                        {JSON.stringify(organizationModrykonik())}
                    </script>
                    {structuredData && structuredData.map((sd, index) => (
                        <script key={`sdata-${index}`} type="application/ld+json">
                            {JSON.stringify(sd)}
                        </script>
                    ))}
                </Helmet>

                {branding}

                <div id="page-wrap">
                    <StickyContainer>
                        <ScrollToTop>
                            <div>
                                {currentUrl === '/login/' && (
                                    // CeMi: nechapem preco je tu a nie vo vnutri login stranky
                                    <PrivacyReminder fluxibleCompatibility={false} currentUrl={currentUrl}/>
                                )}
                                {pageHeader}

                                <div
                                    id="main-wrap"
                                    className={cx(
                                        styles.Page__wrap,
                                        pageInstance.hasFullWidthOnDesktop(this.props) && 'full-width-desktop',
                                    )}
                                >
                                    <div id="page-content">
                                        <div className={this.props.className}>
                                            {this.props.children({
                                                app,
                                                content,
                                                rightContent,
                                                halfpage,
                                            })}
                                        </div>
                                    </div>
                                </div>

                                {pageInstance.hasPageFooter(rest) && <Footer />}
                            </div>
                        </ScrollToTop>
                    </StickyContainer>
                </div>

                <PhoneVerificationModal />
            </div>
        );
    }

    private redirectIfMissingPermission() {
        if (!this.state.hasPermission) {
            const { history, location } = this.props;
            const gotoUrl = url(usersUrls.login, {}, {next: `${location.pathname}${location.search}`});
            redirectInPWA(history, gotoUrl, 401);
        }
    }
}

export default Page;

function hashCode(value) {
    let hash = 0;
    if (value.length === 0) {
        return hash;
    }

    for (let i = 0; i < value.length; i++) {
        const char = value.charCodeAt(i);
        hash = ((hash << 5) - hash) + char; // tslint:disable-line no-bitwise
        // Convert to 32bit integer
        hash = hash & hash; // tslint:disable-line no-bitwise
    }

    return hash;
}
